import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TourCarouselProps {
  images: string[];
  /** Tiempo por imagen antes de auto-avanzar. Default 3000ms. */
  intervalMs?: number;
  /** aria-label de la imagen (ya resuelto por i18n en el consumidor). */
  label?: string;
}

/**
 * Carrusel de imágenes del popover del tour: auto-avanza en bucle con puntitos indicadores.
 * Pensado para mostrar pasos recortados/ampliados a la zona relevante — más claro que un vídeo
 * que se ve pequeño en el hueco del popover. El usuario puede saltar a una imagen con su puntito.
 * Una sola imagen → no muestra puntitos ni auto-avanza.
 */
export function TourCarousel({ images, intervalMs = 3000, label }: TourCarouselProps) {
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // El auto-avance se detiene en cuanto el usuario navega manualmente (flecha o puntito). A partir
  // de ahí solo pasa de imagen con sus controles — no vuelve a moverse solo (patrón estándar).
  const [paused, setPaused] = useState(false);
  const indexRef = useRef(0);
  indexRef.current = index;

  useEffect(() => {
    if (images.length <= 1 || paused) return;
    const id = setInterval(() => {
      setIndex((indexRef.current + 1) % images.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [images.length, intervalMs, paused]);

  if (images.length === 0) return null;

  const hasMany = images.length > 1;
  // Navegación manual: salta a la imagen indicada y pausa el auto-avance.
  const goTo = (i: number) => { setPaused(true); setIndex((i + images.length) % images.length); };
  const goPrev = () => goTo(index - 1);
  const goNext = () => goTo(index + 1);

  return (
    <div className="klonup-tour-carousel">
      <div className="klonup-tour-carousel-frame">
        <img
          className="klonup-tour-media klonup-tour-carousel-image"
          data-testid="carousel-image"
          src={images[index]}
          alt={label ?? ''}
          onClick={() => setLightboxOpen(true)}
        />
        {hasMany && (
          <>
            <button
              type="button"
              data-testid="carousel-prev"
              aria-label="Anterior"
              className="klonup-tour-carousel-arrow klonup-tour-carousel-arrow-prev"
              onClick={goPrev}
            >
              ‹
            </button>
            <button
              type="button"
              data-testid="carousel-next"
              aria-label="Siguiente"
              className="klonup-tour-carousel-arrow klonup-tour-carousel-arrow-next"
              onClick={goNext}
            >
              ›
            </button>
          </>
        )}
      </div>
      {hasMany && (
        <div className="klonup-tour-carousel-dots">
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              data-testid="carousel-dot"
              aria-label={`${i + 1}`}
              aria-current={i === index}
              className={`klonup-tour-carousel-dot${i === index ? ' is-active' : ''}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}

      {/* Lightbox: la captura ampliada sobre un fondo oscuro. Se renderiza en document.body
          (portal) para quedar por encima del popover del tour sin problemas de z-index/clipping.
          Cerrar con la X o click en el fondo NO cierra el tour (stopPropagation en el contenido). */}
      {lightboxOpen && createPortal(
        <div
          className="klonup-tour-lightbox"
          data-testid="carousel-lightbox"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            data-testid="carousel-lightbox-close"
            aria-label="Cerrar"
            className="klonup-tour-lightbox-close"
            onClick={() => setLightboxOpen(false)}
          >
            ×
          </button>
          {hasMany && (
            <button
              type="button"
              data-testid="lightbox-prev"
              aria-label="Anterior"
              className="klonup-tour-carousel-arrow klonup-tour-lightbox-arrow klonup-tour-lightbox-arrow-prev"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
            >
              ‹
            </button>
          )}
          <img
            className="klonup-tour-lightbox-image"
            src={images[index]}
            alt={label ?? ''}
            onClick={(e) => e.stopPropagation()}
          />
          {hasMany && (
            <button
              type="button"
              data-testid="lightbox-next"
              aria-label="Siguiente"
              className="klonup-tour-carousel-arrow klonup-tour-lightbox-arrow klonup-tour-lightbox-arrow-next"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
            >
              ›
            </button>
          )}
          {hasMany && (
            <div className="klonup-tour-lightbox-dots" onClick={(e) => e.stopPropagation()}>
              {images.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  data-testid="lightbox-dot"
                  aria-label={`${i + 1}`}
                  aria-current={i === index}
                  className={`klonup-tour-carousel-dot${i === index ? ' is-active' : ''}`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
